import { ADMIN_PATH } from '@shared/admin';
import { creditsResetLabel, describeCredits } from '@shared/ai/credits';
import type { UsageSummary } from '@shared/api';
import { planFor } from '@shared/plans';
import type { ApiKeyStore } from '@/capture/ApiKeyStore';
import { CloudError, type CloudSession, type SessionState } from '@/cloud';
import { googleSignInButton, injectGoogleSignInStyles } from '@/cloud/GoogleSignInButton';
import { countLocalOnly, describeImport, importLocalProjects } from '@/cloud/LocalImport';
import type { ProjectStorage } from '@/persistence/ProjectStorage';
import { Dialog, dialogButton, dialogSummaryRow, textInput } from './Dialog';
import { clear, el } from './dom';
import { icon } from './Icons';

/**
 * The account control in the toolbar, and the dialog behind it.
 *
 * It is the one piece of UI that only exists on a hosted deployment, and it stays honest about
 * which deployment it is in: with no server reachable, `authAvailable` is false and this renders
 * nothing at all — no dead sign-in button, no hint of a feature the build does not have. The
 * original single-user tool is unchanged down to the pixel.
 *
 * Signed in, the dialog answers the three questions an account raises, in the order a user asks
 * them: where is my work kept, what happened to the sites I made before I signed in, and what is
 * this costing me.
 */

export interface AccountMenuDeps {
  host: HTMLElement;
  session: CloudSession;
  /** The credential store, switched to managed mode while signed in. */
  apiKeys: ApiKeyStore;
  /** The account-backed project store, for the local-import offer. Null before sign-in. */
  cloudProjects: () => ProjectStorage | null;
  /** Shown as a toast — reuses the project menu's notifier so messages land in one place. */
  notify: (message: string) => void;
  /** Re-read the library after an import brings sites in. */
  onLibraryChanged?: () => void;
}

export class AccountMenu {
  readonly root: HTMLElement;

  private dialog: Dialog | null = null;
  private readonly button: HTMLButtonElement;
  private importing = false;

  constructor(private readonly deps: AccountMenuDeps) {
    this.button = el('button', {
      class: 'lg-iconbtn lg-account__button',
      attrs: { type: 'button' },
      on: { click: () => this.open() },
    });

    this.root = el('div', { class: 'lg-account' }, this.button);
    this.render(deps.session.current);
    deps.session.onChange((state) => this.render(state));
  }

  /** Reflect the session in the toolbar button — avatar and name, or a sign-in prompt. */
  private render(state: SessionState): void {
    // A build with no auth configured shows nothing rather than a button that cannot work.
    this.root.hidden = !state.authAvailable;
    if (!state.authAvailable) return;

    clear(this.button);

    if (state.user === null) {
      this.button.title = 'Sign in to save your sites to your account';
      this.button.setAttribute('aria-label', 'Sign in');
      this.button.appendChild(el('span', { class: 'lg-account__icon', html: icon('open', 15) }));
      this.button.appendChild(el('span', { class: 'lg-account__label', text: 'Sign in' }));
      return;
    }

    this.button.title = `Signed in as ${state.user.email}`;
    this.button.setAttribute('aria-label', `Account — ${state.user.email}`);

    if (state.user.pictureUrl !== undefined) {
      this.button.appendChild(
        el('img', {
          class: 'lg-account__avatar',
          attrs: {
            src: state.user.pictureUrl,
            alt: '',
            // Google serves these; a failure must not leave a broken-image icon in the toolbar.
            referrerpolicy: 'no-referrer',
          },
        }),
      );
    } else {
      this.button.appendChild(
        el('span', { class: 'lg-account__initial', text: initial(state.user.name) }),
      );
    }
    this.button.appendChild(
      el('span', { class: 'lg-account__label', text: firstName(state.user.name) }),
    );
  }

  open(): void {
    const state = this.deps.session.current;
    if (!state.authAvailable) return;

    this.dialog?.dispose();
    const dialog = new Dialog({
      host: this.deps.host,
      title: state.user ? 'Your account' : 'Sign in',
      icon: 'open',
      subtitle: state.user
        ? 'Your sites are kept here and open on any machine you sign in on.'
        : 'Sign in with Google to keep your sites in your account.',
      onClose: () => {
        this.dialog = null;
      },
    });
    this.dialog = dialog;

    if (state.user === null) {
      this.renderSignedOut(dialog);
    } else {
      this.renderSignedIn(dialog, state);
    }
    dialog.show();
  }

  private renderSignedOut(dialog: Dialog): void {
    dialog.body.appendChild(
      el('p', {
        class: 'lg-hint',
        text:
          'Signing in keeps your sites in your account instead of in this browser, so they open on ' +
          'any machine. Anything you have already made on this device stays where it is — you will ' +
          'be offered the chance to copy it across.',
      }),
    );

    // The same component the sign-in wall uses, not a dialog button wearing a folder icon. A user
    // who has seen one of these two screens should recognise the control on the other, and there is
    // only one place the mark is allowed to come from.
    injectGoogleSignInStyles();
    dialog.setActions(
      dialogButton({ label: 'Not now', onClick: () => dialog.close() }),
      googleSignInButton({
        // Signing in here is an upgrade rather than a requirement, which is the case Google's
        // "Continue with" wording is for. The wall says "Sign in".
        label: 'Continue with Google',
        onSignIn: () => this.deps.session.signIn(),
      }),
    );
  }

  private renderSignedIn(dialog: Dialog, state: SessionState): void {
    const user = state.user;
    /* c8 ignore next */
    if (!user) return;

    dialog.body.appendChild(
      el(
        'div',
        { class: 'lg-dialog__summary' },
        dialogSummaryRow('Signed in as', user.email),
        dialogSummaryRow('Sites', 'Saved to your account'),
        dialogSummaryRow('Shots', 'Saved to your account'),
      ),
    );

    dialog.body.appendChild(this.usageSection(state.usage, state.platformAiAvailable));

    // Offered rather than done automatically: uploading someone's library is their decision, and a
    // silent bulk transfer on first sign-in is exactly the kind of surprise that erodes trust.
    const importSlot = el('div', { class: 'lg-account__import' });
    dialog.body.appendChild(importSlot);
    void this.renderImportOffer(importSlot);

    // A link rather than a button, and only for an operator. The console is its own document at
    // `/admin`, so this is a navigation and should behave like one — middle-click, open in a new
    // tab, the lot.
    if (state.isAdmin) {
      dialog.body.appendChild(
        el(
          'p',
          { class: 'lg-hint' },
          el('a', {
            text: 'Open the operator console',
            attrs: { href: ADMIN_PATH, rel: 'noopener' },
          }),
        ),
      );
    }

    dialog.setActions(
      dialogButton({ label: 'Close', onClick: () => dialog.close() }),
      dialogButton({
        label: 'Sign out',
        onClick: () => {
          void this.deps.session.signOut().then(() => {
            this.deps.apiKeys.setManaged(false);
            this.deps.notify('Signed out. Sites are being saved on this device again.');
            dialog.close();
          });
        },
      }),
    );
  }

  /**
   * Credit grants on this account, when there are any.
   *
   * Renders nothing at all on the ordinary account, which is most of them — a heading over an empty
   * list would invite the question "should I have some of those?" and answer it badly.
   *
   * Why show them at all, when the total is already folded into the allowance above: because a
   * grant is the difference between an allowance the user can rely on next month and one that is
   * about to fall back. The expiry is the whole point of the row, and the reason the admin console
   * requires a reason for every grant is that this is where that reason is read.
   *
   * Grants are shown to an own-key account too. They are dormant there — an own-key call is never
   * billable, so nothing draws them down — but they are still on the account, and quietly hiding
   * them would make the balance appear to vanish the moment a key is pasted.
   */
  private appendGrants(section: HTMLElement, usage: UsageSummary | null): void {
    // `?? []` rather than a required field: a tab left open across the deploy that added `grants`
    // is talking to a server that does not send it, and the account dialog is not worth a crash.
    const grants = usage?.grants ?? [];
    if (grants.length === 0) return;

    const total = grants.reduce((sum, grant) => sum + grant.credits, 0);
    section.appendChild(
      el('p', {
        class: 'lg-hint',
        text: `${String(total)} of those credits ${
          grants.length === 1 ? 'comes from a grant' : 'come from grants'
        } on your account:`,
      }),
    );

    const list = el('ul', { class: 'lg-account__grants' });
    for (const grant of grants) {
      list.appendChild(
        el(
          'li',
          { class: 'lg-account__grant' },
          el('span', {
            class: 'lg-account__grantcredits',
            text: `${String(grant.credits)} credit${grant.credits === 1 ? '' : 's'}`,
          }),
          el('span', {
            class: 'lg-account__grantwhen',
            // A standing grant and a lapsing one are different arrangements, not the same one with
            // a blank date, so they get different words rather than an em dash for the missing part.
            text:
              grant.expiresAt === null
                ? 'every month, until cancelled'
                : `until ${formatGrantDate(grant.expiresAt)}`,
          }),
          el('span', { class: 'lg-account__grantreason', text: grant.reason }),
        ),
      );
    }
    section.appendChild(list);
  }

  /**
   * The allowance readout and the bring-your-own-key field.
   *
   * Both are shown together because they are one decision: the number tells you whether you need
   * the field. A user on their own key sees no allowance at all, because it does not apply to them.
   */
  private usageSection(usage: UsageSummary | null, platformAvailable: boolean): HTMLElement {
    const section = el('div', { class: 'lg-account__usage' });

    // The plan is stated on its own line and before the balance, because it is the fact that
    // explains the balance. "60 of 200 credits" prompts "is 200 a lot?", and only the plan name
    // answers it. Shown on every branch, including an own-key account: a key removes the cap, not
    // the subscription.
    if (usage) {
      const plan = planFor(usage.plan);
      section.appendChild(
        el('p', {
          class: 'lg-hint',
          text:
            plan.priceUsdPerMonth === 0
              ? `You are on the ${plan.name} plan.`
              : `You are on the ${plan.name} plan, $${String(plan.priceUsdPerMonth)} a month.`,
        }),
      );
    }

    if (usage?.hasOwnKey === true) {
      section.appendChild(
        el('p', {
          class: 'lg-hint',
          text:
            `AI renders run on your own OpenAI key (${usage.ownKeyHint ?? 'stored'}), so there is no ` +
            'monthly cap and the usage appears on your OpenAI bill. The key is encrypted and never ' +
            'sent to your browser.',
        }),
      );
    } else if (!platformAvailable) {
      section.appendChild(
        el('p', {
          class: 'lg-hint',
          text:
            'This deployment has no shared OpenAI key, so the AI features need one of your own. ' +
            'Paste it below — it is encrypted on the server and never returned to the browser.',
        }),
      );
    } else if (usage) {
      const limit = usage.creditsLimit;
      const remaining = limit === null ? null : Math.max(0, limit - usage.creditsUsed);
      section.appendChild(
        el('p', {
          class: 'lg-hint',
          text:
            limit === null || remaining === null
              ? `${String(usage.creditsUsed)} credits used this month. No cap on this deployment.`
              : remaining === 0
                ? // Out of credits, the balance is no longer the news — the date is. Saying "0 left,
                  // none left this month" would spend the sentence restating the zero instead of
                  // telling the user the one thing they now want, which is when it stops being true.
                  `All ${String(limit)} credits for this month are spent. They reset on ` +
                  `${creditsResetLabel(usage.period)}. Add your own OpenAI key below to carry on now.`
                : // Credits, then what they are worth, then when they come back. The middle is the
                  // part anyone actually reads: a bare balance says nothing until you know what it
                  // buys, and a token count would say nothing at all — which is why one is never
                  // shown here.
                  `${String(usage.creditsUsed)} of ${String(limit)} credits used this month — ` +
                  `${String(remaining)} left, ${describeCredits(remaining)}. Resets on ` +
                  `${creditsResetLabel(usage.period)}. Add your own OpenAI key to lift the cap.`,
        }),
      );
    }

    this.appendGrants(section, usage);

    const field = textInput({
      value: '',
      placeholder: usage?.hasOwnKey === true ? 'Replace stored key…' : 'sk-…',
      ariaLabel: 'Your OpenAI API key',
    });
    field.type = 'password';

    const row = el(
      'div',
      { class: 'lg-account__keyrow' },
      field,
      dialogButton({
        label: 'Save key',
        onClick: () => {
          void this.storeKey(field.value, section);
        },
      }),
    );
    section.appendChild(row);

    if (usage?.hasOwnKey === true) {
      section.appendChild(
        dialogButton({
          label: 'Remove my key',
          danger: true,
          onClick: () => {
            void this.storeKey('', section);
          },
        }),
      );
    }

    return section;
  }

  private async storeKey(value: string, section: HTMLElement): Promise<void> {
    try {
      const usage = await this.deps.session.setOwnApiKey(value);
      this.deps.apiKeys.setManaged(true, describeCredential(usage));
      this.deps.notify(
        value.trim().length === 0
          ? 'Your key was removed. AI renders use the included allowance again.'
          : 'Key saved. AI renders now run on your own OpenAI account.',
      );
      // Redraw in place so the allowance line and the remove button match the new state.
      const replacement = this.usageSection(usage, this.deps.session.current.platformAiAvailable);
      section.replaceWith(replacement);
    } catch (error) {
      this.deps.notify(
        error instanceof CloudError ? error.message : 'Could not save that key.',
      );
    }
  }

  /**
   * Offer to copy anything made on this device before signing in.
   *
   * Rendered async because it has to compare both libraries to know whether there is anything to
   * offer, and there usually is not — an account that has been in use for a while has already
   * absorbed whatever was here.
   */
  private async renderImportOffer(slot: HTMLElement): Promise<void> {
    const cloud = this.deps.cloudProjects();
    if (!cloud) return;

    const pending = await countLocalOnly(cloud);
    if (pending === 0 || this.importing) return;

    clear(slot);
    const status = el('span', { class: 'lg-hint' });
    const button = dialogButton({
      label: `Copy ${String(pending)} site${pending === 1 ? '' : 's'} to my account`,
      icon: 'save',
      onClick: () => {
        void this.runImport(cloud, status, button);
      },
    });

    slot.appendChild(
      el('p', {
        class: 'lg-hint',
        text:
          `There ${pending === 1 ? 'is 1 site' : `are ${String(pending)} sites`} saved in this ` +
          'browser that your account does not have. Copying leaves the originals on this device.',
      }),
    );
    slot.appendChild(button);
    slot.appendChild(status);
  }

  private async runImport(
    cloud: ProjectStorage,
    status: HTMLElement,
    button: HTMLButtonElement,
  ): Promise<void> {
    this.importing = true;
    button.disabled = true;

    try {
      const result = await importLocalProjects({
        cloud,
        onProgress: (progress) => {
          status.textContent =
            progress.current.length > 0
              ? `Copying “${progress.current}” — ${String(progress.done + 1)} of ${String(progress.total)}…`
              : 'Finishing…';
        },
      });
      status.textContent = describeImport(result);
      this.deps.notify(describeImport(result));
      this.deps.onLibraryChanged?.();
      // Re-offering makes sense only if something failed and could be retried.
      if (result.failed.length === 0) button.remove();
      else button.disabled = false;
    } catch (error) {
      status.textContent =
        error instanceof Error ? `Copy failed — ${error.message}` : 'Copy failed.';
      button.disabled = false;
    } finally {
      this.importing = false;
    }
  }

  dispose(): void {
    this.dialog?.dispose();
    this.root.remove();
  }
}

/** What the Enhance panel shows in place of a key field while the server holds the credential. */
export function describeCredential(usage: UsageSummary | null): string {
  if (usage?.hasOwnKey === true) return `your key (${usage.ownKeyHint ?? 'stored'})`;
  const limit = usage?.creditsLimit ?? null;
  if (usage === null || limit === null) return 'your account';
  return `your account — ${String(Math.max(0, limit - usage.creditsUsed))} credits left`;
}

/**
 * A grant's expiry, as a date rather than a timestamp.
 *
 * Local time on purpose, unlike the allowance reset: an expiry is a real instant the server chose
 * and stored, so the user's own calendar is the right frame to read it in. The reset date is the
 * opposite case — a month boundary in the database's timezone — which is why the two are formatted
 * by different code rather than sharing a helper that would have to be wrong for one of them.
 */
function formatGrantDate(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  return when.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function initial(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}
